/**
 * PolicyModal Component
 * Wraps the shared Modal component to display policy content fetched from the API (via the shared
 * usePolicyDocument hook). Uses MDEditor.Markdown for rendering (same library as RulesSection).
 */

import Modal from './Modal';
import MDEditor from '@uiw/react-md-editor';
import { usePolicyDocument } from '@resida/consent-ui';
import type { DocumentPolicyType } from '@resida/shared';
import { MarkdownThemeScope } from './MarkdownThemeScope';
import { Button } from './ui/button';

const POLICY_TITLES: Record<DocumentPolicyType, string> = {
  platform_terms: 'Terms of Use',
  privacy_policy: 'Privacy Policy',
  landlord_supplement: 'Landlord Supplemental Terms',
  tenant_supplement: 'Tenant Supplemental Terms',
  acceptable_use: 'Acceptable Use Policy',
};

interface PolicyModalProps {
  policyType: DocumentPolicyType;
  isOpen: boolean;
  onClose: () => void;
}

export function PolicyModal({ policyType, isOpen, onClose }: PolicyModalProps) {
  const { status, document, retry } = usePolicyDocument(isOpen ? policyType : null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={POLICY_TITLES[policyType]}
    >
      <div
        className="max-h-[60vh] overflow-y-auto px-1"
        data-testid={`policy-modal-content-${policyType}`}
      >
        {status === 'loading' && <p className="text-sm text-muted-foreground">Loading...</p>}
        {status === 'error' && (
          <div role="alert" className="flex flex-col items-start gap-3">
            <p className="text-sm text-destructive">Failed to load policy content.</p>
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={retry}
              data-testid="policy-modal-retry-button"
            >
              Try again
            </Button>
          </div>
        )}
        {status === 'ready' && document && (
          <MarkdownThemeScope>
            <MDEditor.Markdown
              source={document.content}
              style={{ backgroundColor: 'transparent' }}
            />
          </MarkdownThemeScope>
        )}
      </div>
    </Modal>
  );
}

export default PolicyModal;
